/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface TripProps {
  id: string;
  vehicleId: string | null;
  agencyId: string | null;
  agencyLocationId: string | null;
  calculatedLoadedMiles: number | null;
  gpsLoadedMiles: number | null;
  operationId: string;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class TripRecord extends Record<TripProps>({
  id: null as any,
  vehicleId: null as any,
  agencyId: null as any,
  agencyLocationId: null as any,
  calculatedLoadedMiles: null as any,
  gpsLoadedMiles: null as any,
  operationId: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    vehicleId: string | null;
    agencyId: string | null;
    agencyLocationId: string | null;
    calculatedLoadedMiles: number | null;
    gpsLoadedMiles: number | null;
    operationId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): TripRecord {
    if (json == null) {
      throw new Error('TripRecord.fromJS: parameter cannot be null');
    }
    return new TripRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'TripJson.id',
      ),
      vehicleId: json.vehicleId,
      agencyId: json.agencyId,
      agencyLocationId: json.agencyLocationId,
      calculatedLoadedMiles: json.calculatedLoadedMiles,
      gpsLoadedMiles: json.gpsLoadedMiles,
      operationId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'operationId', '00000000-0000-0000-0000-000000000000'),
        'TripJson.operationId',
      ),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'TripJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    vehicleId: string | null;
    agencyId: string | null;
    agencyLocationId: string | null;
    calculatedLoadedMiles: number | null;
    gpsLoadedMiles: number | null;
    operationId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [TripRecord, Error[]] {
    if (json == null) {
      throw new Error('TripRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new TripRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'TripJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      vehicleId: tryOrDefault(
        () => json.vehicleId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      agencyId: tryOrDefault(
        () => json.agencyId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      agencyLocationId: tryOrDefault(
        () => json.agencyLocationId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      calculatedLoadedMiles: tryOrDefault(
        () => json.calculatedLoadedMiles,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      gpsLoadedMiles: tryOrDefault(
        () => json.gpsLoadedMiles,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      operationId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'operationId', '00000000-0000-0000-0000-000000000000'),
            'TripJson.operationId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'TripJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: TripProps) {
    super(props);
    if (props == null) {
      throw new Error('TripRecord constructor parameter cannot be null');
    }
  }
}

export abstract class TripViewModelBase implements TripProps {
  protected readonly _props: TripProps;
  private _json?: TripProps;

  get id(): string {
    return this._props.id;
  }
  get vehicleId(): string | null {
    return this._props.vehicleId;
  }
  get agencyId(): string | null {
    return this._props.agencyId;
  }
  get agencyLocationId(): string | null {
    return this._props.agencyLocationId;
  }
  get calculatedLoadedMiles(): number | null {
    return this._props.calculatedLoadedMiles;
  }
  get gpsLoadedMiles(): number | null {
    return this._props.gpsLoadedMiles;
  }
  get operationId(): string {
    return this._props.operationId;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: TripProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        vehicleId: this._props.vehicleId,
        agencyId: this._props.agencyId,
        agencyLocationId: this._props.agencyLocationId,
        calculatedLoadedMiles: this._props.calculatedLoadedMiles,
        gpsLoadedMiles: this._props.gpsLoadedMiles,
        operationId: this._props.operationId,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllTripEntities = (state: AppState) => state.entityStores.trip.entities;

export const selectTripById = (id: string) => (state: AppState) =>
  state.entityStores.trip.entities.get(id);

export const selectTripMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.trip.meta.get(id);

export const selectTripAndMetadataById = (id: string) =>
  createSelector(selectTripById(id), selectTripMetadataById(id), (trip, meta) => ({ trip, meta }));

export function selectTripEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllTripEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, TripRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectTripEntitiesByVehicleId(vehicleId: string) {
  return createSelector(selectAllTripEntities, (entities) =>
    entities.filter((e) => e.vehicleId === vehicleId).valueSeq(),
  );
}

export function selectTripEntitiesByAgencyId(agencyId: string) {
  return createSelector(selectAllTripEntities, (entities) =>
    entities.filter((e) => e.agencyId === agencyId).valueSeq(),
  );
}

export function selectTripEntitiesByAgencyLocationId(agencyLocationId: string) {
  return createSelector(selectAllTripEntities, (entities) =>
    entities.filter((e) => e.agencyLocationId === agencyLocationId).valueSeq(),
  );
}

export function selectTripEntitiesByCalculatedLoadedMiles(calculatedLoadedMiles: number) {
  return createSelector(selectAllTripEntities, (entities) =>
    entities.filter((e) => e.calculatedLoadedMiles === calculatedLoadedMiles).valueSeq(),
  );
}

export function selectTripEntitiesByGpsLoadedMiles(gpsLoadedMiles: number) {
  return createSelector(selectAllTripEntities, (entities) =>
    entities.filter((e) => e.gpsLoadedMiles === gpsLoadedMiles).valueSeq(),
  );
}

export function selectTripEntitiesByOperationId(operationId: string) {
  return createSelector(selectAllTripEntities, (entities) =>
    entities.filter((e) => e.operationId === operationId).valueSeq(),
  );
}

export function selectTripEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllTripEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
